import { useRef } from "react";

/**
 * Sticky "has this gate ever finished resolving?" flag.
 *
 * Route guards need to tell an *initial* load (nothing decided yet — the loader
 * is the only honest thing to render) apart from a *revalidation* (we already
 * resolved once, and a background refresh must not unmount the whole route
 * subtree behind a full-page spinner). Supabase fires an auth event whenever the
 * tab regains focus, so without this distinction every tab switch blanked the
 * app and remounted every child.
 *
 * Tracked in a ref rather than state so flipping it never itself causes a
 * render. The write happens during render, which is safe here because it is
 * monotonic and idempotent — it only ever goes false -> true, and re-running the
 * render (StrictMode, a discarded concurrent render) produces the same value.
 *
 * @param isResolved whether the caller's checks have settled *this* render
 * @returns true once `isResolved` has been true at least once
 */
export const useHasResolvedOnce = (isResolved: boolean): boolean => {
  const resolvedOnce = useRef(false);
  if (isResolved) resolvedOnce.current = true;
  return resolvedOnce.current;
};
